import { DatacentralHookForm } from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    createConfigOptionPrice,
    updateConfigOptionPrice,
} from '@/modules/Service/services'
import type { ConfigOptionPriceHistory } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface ConfigOptionPriceModalProps extends ModalProps {
    serviceID: number
    configID: number
    configName?: string
    price?: ConfigOptionPriceHistory
}

interface FormValues {
    commitment_value: number
    commitment_price_per_unit: number
    effective_from: string
    effective_to: string | null
    description: string
}

const ConfigOptionPriceModal: React.FC<ConfigOptionPriceModalProps> = ({
    open,
    onClose,
    serviceID,
    configID,
    configName,
    price,
}) => {
    const { t } = useTranslation('services')
    const { t: tCommon } = useTranslation()
    const isEdit = !!price

    const formControl = useForm<FormValues>({
        defaultValues: {
            commitment_value: price?.commitment_value || 0,
            commitment_price_per_unit: price?.commitment_price_per_unit || 0,
            effective_from:
                price?.effective_from || dayjs().format('YYYY-MM-DD'),
            effective_to: price?.effective_to || null,
            description: price?.description || '',
        },
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY],
    })

    const { mutateAsync: createMutation, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createConfigOptionPrice },
            {
                successMessage: t('configoption_prices.create_success'),
                reactQueryKeys: QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const { mutateAsync: updateMutation, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateConfigOptionPrice },
            {
                successMessage: t('configoption_prices.update_success'),
                reactQueryKeys: QUERY_KEYS.SERVICES_CONFIGOPTION_PRICE_HISTORY,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = (values: FormValues) => {
        const data = {
            commitment_value: Number(values.commitment_value),
            commitment_price_per_unit: Number(values.commitment_price_per_unit),
            effective_from: values.effective_from,
            effective_to: values.effective_to || undefined,
            description: values.description || undefined,
        }

        if (isEdit && price) {
            updateMutation({
                serviceID,
                configID,
                priceID: price.id,
                data,
            })
        } else {
            createMutation({ serviceID, configID, data })
        }
    }

    const title = isEdit
        ? t('configoption_prices.edit')
        : t('configoption_prices.add')

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={configName ? `${title} — ${configName}` : title}
        >
            <DatacentralHookForm<FormValues>
                formControl={formControl}
                onSubmit={onSubmit}
                columns={2}
                data={[
                    {
                        formItemProps: {
                            name: 'commitment_value',
                            label: t('configoption_prices.commitment_value'),
                            type: 'number',
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'commitment_price_per_unit',
                            label: t('configoption_prices.price_per_unit'),
                            type: 'number',
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'effective_from',
                            label: t('configoption_prices.effective_from'),
                            type: 'datepicker',
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'effective_to',
                            label: t('configoption_prices.effective_to'),
                            type: 'datepicker',
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'description',
                            label: t('configoption_prices.description'),
                            type: 'textarea',
                        },
                        colSpan: 2,
                    },
                ]}
                actions={[
                    {
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        title: tCommon('cancel'),
                    },
                    {
                        type: 'submit',
                        variant: 'default',
                        title: tCommon('save'),
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default ConfigOptionPriceModal
